// ---------------------------------------------------------------------------
// google-pay.content.ts — Localized content for Google Pay integration landing page
// ---------------------------------------------------------------------------
import type { IntegrationRecord } from './types';

export const GOOGLE_PAY_INTEGRATION: IntegrationRecord = {
  constants: { slug: 'google-pay', partnerName: 'Google Pay' },
  content: {
    en: {
      partnerType: 'Contactless Payment',
      heroTitle: 'HotelStack.io + Google Pay Integration',
      heroSubtitle: 'Accept contactless payments from 150M+ Google Pay users in 40+ countries. Seamless checkout on Android devices, Chrome browser, and Google services.',
      benefits: [
        {
          title: '150M+ Users in 40+ Countries',
          description: 'Google Pay is the leading mobile payment solution on Android. Reach the massive Android user base with one-tap payment across web and mobile.',
        },
        {
          title: 'Chrome Browser Payments',
          description: 'Google Pay works natively in Chrome browser on desktop and mobile. Guests pay with saved cards via Google Account — no app installation needed.',
        },
        {
          title: 'UPI Integration (India)',
          description: 'In India, Google Pay is the #1 UPI app with 200M+ users. Accept UPI payments seamlessly through the Google Pay integration for Indian domestic guests.',
        },
        {
          title: 'Reduced Cart Abandonment',
          description: 'Google Pay reduces checkout friction by 40%. Auto-filled payment credentials and one-tap confirmation minimize booking drop-off rates.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'Enable via Payment Gateway',
          description: 'Google Pay is enabled through your existing Stripe, PayPal, or PaymentHooks integration. No separate Google merchant account required for most setups.',
        },
        {
          step: 2,
          title: 'Configure Display Rules',
          description: 'Choose where Google Pay appears: booking engine checkout, front desk payments, guest portal charges, and payment link flows.',
        },
        {
          step: 3,
          title: 'Set Currency Preferences',
          description: "Configure supported currencies and localization settings. Google Pay auto-detects the guest's preferred payment method by region.",
        },
        {
          step: 4,
          title: 'Go Live',
          description: 'The Google Pay button appears for eligible guests on Android devices, Chrome browser, and your booking engine. Payments process instantly.',
        },
      ],
      features: [
        {
          title: 'Booking Engine Button',
          description: 'Google Pay button on your BookMyRoom.io checkout. Android and Chrome users see the familiar GPay option for instant payment.',
        },
        {
          title: 'UPI Payments (India)',
          description: 'Accept Google Pay UPI transactions for Indian guests. Supports both scan-and-pay and intent-based UPI flows through PaymentHooks.',
        },
        {
          title: 'NFC Terminal Support',
          description: 'Accept Google Pay tap-to-pay at your front desk with NFC-enabled payment terminals. Contactless check-in and check-out experience.',
        },
        {
          title: 'Saved Card Autofill',
          description: 'Guests pay with cards saved in their Google Account. No manual entry needed — just select a card and authenticate.',
        },
        {
          title: 'Cross-Platform Support',
          description: 'Google Pay works on Android phones, tablets, Wear OS watches, and Chrome browser on any operating system including Windows, Mac, and Linux.',
        },
        {
          title: 'Payment Analytics',
          description: 'Track Google Pay adoption rates, transaction volumes, and average booking values alongside all other payment methods in your finance dashboard.',
        },
      ],
      faqItems: [
        {
          question: 'Do I need a Google merchant account?',
          answer: 'For most setups, no. Google Pay is enabled through your existing Stripe, PayPal, or PaymentHooks integration. If you need advanced Google Pay features, a Google Pay Business Console account can be set up at no cost.',
        },
        {
          question: 'Does Google Pay work with UPI in India?',
          answer: 'Yes. Google Pay is the #1 UPI app in India with 200M+ users. When combined with PaymentHooks integration, you can accept Google Pay UPI payments seamlessly for Indian domestic guests.',
        },
        {
          question: 'Are there additional fees for Google Pay?',
          answer: 'Google does not charge merchants additional fees for Google Pay transactions. You pay the standard fees of your payment gateway (Stripe, PayPal, or PaymentHooks). No HotelStack.io surcharge applies.',
        },
        {
          question: 'What percentage of guests use Google Pay?',
          answer: 'Adoption varies by market. In India, 40-50% of mobile payments use Google Pay (UPI). Globally, 15-25% of Android users have Google Pay configured, with higher adoption in Southeast Asia and Europe.',
        },
      ],
    },
    ar: {
      partnerType: 'دفع لا تلامسي',
      heroTitle: 'تكامل HotelStack.io مع Google Pay',
      heroSubtitle: 'اقبل المدفوعات اللاتلامسية من أكثر من ١٥٠ مليون مستخدم Google Pay في أكثر من ٤٠ دولة. دفع سلس عند الخروج على أجهزة Android ومتصفح Chrome وخدمات Google.',
      benefits: [
        {
          title: 'أكثر من ١٥٠ مليون مستخدم في ٤٠+ دولة',
          description: 'Google Pay هو الحل الرائد للمدفوعات عبر الهاتف المحمول على Android. تواصل مع قاعدة مستخدمي Android الضخمة بدفع بنقرة واحدة عبر الويب والهاتف.',
        },
        {
          title: 'مدفوعات متصفح Chrome',
          description: 'يعمل Google Pay بشكل أصلي في متصفح Chrome على سطح المكتب والهاتف المحمول. يدفع النزلاء بالبطاقات المحفوظة عبر حساب Google — دون الحاجة لتثبيت أي تطبيق.',
        },
        {
          title: 'تكامل UPI (الهند)',
          description: 'في الهند، Google Pay هو تطبيق UPI الأول بأكثر من ٢٠٠ مليون مستخدم. اقبل مدفوعات UPI بسلاسة عبر تكامل Google Pay للنزلاء المحليين الهنود.',
        },
        {
          title: 'تقليل التخلي عن الحجز',
          description: 'يقلل Google Pay من عوائق الدفع بنسبة ٤٠%. تملأ بيانات الدفع تلقائياً والتأكيد بنقرة واحدة مما يقلل معدلات التخلي عن الحجز.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'التفعيل عبر بوابة الدفع',
          description: 'يُفعَّل Google Pay من خلال تكامل Stripe أو PayPal أو PaymentHooks الحالي. لا حاجة لحساب تاجر Google منفصل لمعظم الإعدادات.',
        },
        {
          step: 2,
          title: 'تكوين قواعد العرض',
          description: 'اختر أين يظهر Google Pay: الدفع في محرك الحجز، ومدفوعات مكتب الاستقبال، ورسوم بوابة النزلاء، وتدفقات رابط الدفع.',
        },
        {
          step: 3,
          title: 'تعيين تفضيلات العملة',
          description: 'كوّن العملات المدعومة وإعدادات الترجمة. يكتشف Google Pay تلقائياً طريقة الدفع المفضلة للنزيل حسب المنطقة.',
        },
        {
          step: 4,
          title: 'البدء المباشر',
          description: 'يظهر زر Google Pay للنزلاء المؤهلين على أجهزة Android ومتصفح Chrome ومحرك الحجز الخاص بك. تتم معالجة المدفوعات فورياً.',
        },
      ],
      features: [
        {
          title: 'زر محرك الحجز',
          description: 'زر Google Pay على صفحة الدفع في BookMyRoom.io. يرى مستخدمو Android و Chrome خيار GPay المألوف للدفع الفوري.',
        },
        {
          title: 'مدفوعات UPI (الهند)',
          description: 'اقبل معاملات Google Pay UPI للنزلاء الهنود. يدعم كلاً من تدفق المسح والدفع وتدفقات UPI المستندة إلى النية عبر PaymentHooks.',
        },
        {
          title: 'دعم محطات NFC',
          description: 'اقبل الدفع اللاتلامسي عبر Google Pay في مكتب الاستقبال باستخدام أجهزة الدفع المدعومة بـ NFC. تجربة تسجيل وصول ومغادرة لاتلامسية.',
        },
        {
          title: 'الملء التلقائي للبطاقات المحفوظة',
          description: 'يدفع النزلاء بالبطاقات المحفوظة في حساب Google الخاص بهم. لا حاجة للإدخال اليدوي — فقط اختر بطاقة وتحقق من هويتك.',
        },
        {
          title: 'دعم متعدد المنصات',
          description: 'يعمل Google Pay على هواتف Android والأجهزة اللوحية وساعات Wear OS ومتصفح Chrome على أي نظام تشغيل بما في ذلك Windows و Mac و Linux.',
        },
        {
          title: 'تحليلات المدفوعات',
          description: 'تتبع معدلات تبني Google Pay وأحجام المعاملات ومتوسط قيم الحجز جنباً إلى جنب مع جميع طرق الدفع الأخرى في لوحة المالية.',
        },
      ],
      faqItems: [
        {
          question: 'هل أحتاج إلى حساب تاجر Google؟',
          answer: 'في معظم الإعدادات، لا. يُفعَّل Google Pay من خلال تكامل Stripe أو PayPal أو PaymentHooks الحالي. إذا كنت بحاجة إلى ميزات Google Pay المتقدمة، يمكن إعداد حساب Google Pay Business Console مجاناً.',
        },
        {
          question: 'هل يعمل Google Pay مع UPI في الهند؟',
          answer: 'نعم. Google Pay هو تطبيق UPI الأول في الهند بأكثر من ٢٠٠ مليون مستخدم. عند دمجه مع تكامل PaymentHooks، يمكنك قبول مدفوعات Google Pay UPI بسلاسة للنزلاء المحليين الهنود.',
        },
        {
          question: 'هل هناك رسوم إضافية على Google Pay؟',
          answer: 'لا تفرض Google رسوماً إضافية على التجار لمعاملات Google Pay. تدفع الرسوم القياسية لبوابة الدفع الخاصة بك (Stripe أو PayPal أو PaymentHooks). لا تنطبق أي رسوم إضافية من HotelStack.io.',
        },
        {
          question: 'ما النسبة المئوية للنزلاء الذين يستخدمون Google Pay؟',
          answer: 'يتفاوت التبني حسب السوق. في الهند، تستخدم ٤٠-٥٠% من المدفوعات عبر الهاتف Google Pay (UPI). على مستوى العالم، يمتلك ١٥-٢٥% من مستخدمي Android تكوين Google Pay، مع تبني أعلى في جنوب شرق آسيا وأوروبا.',
        },
      ],
    },
    th: {
      partnerType: 'การชำระเงินแบบไม่ต้องสัมผัส',
      heroTitle: 'การเชื่อมต่อ HotelStack.io กับ Google Pay',
      heroSubtitle: 'รับชำระเงินแบบไม่ต้องสัมผัสจากผู้ใช้ Google Pay กว่า 150 ล้านคนใน 40+ ประเทศ ชำระเงินได้อย่างราบรื่นบนอุปกรณ์ Android, Chrome browser และบริการ Google',
      benefits: [
        {
          title: 'ผู้ใช้กว่า 150 ล้านคนใน 40+ ประเทศ',
          description: 'Google Pay คือโซลูชันการชำระเงินผ่านมือถือชั้นนำบน Android เข้าถึงฐานผู้ใช้ Android ขนาดใหญ่ด้วยการชำระเงินแตะครั้งเดียวทั้งบนเว็บและมือถือ',
        },
        {
          title: 'การชำระเงินผ่าน Chrome Browser',
          description: 'Google Pay ทำงานได้โดยตรงใน Chrome browser บนคอมพิวเตอร์และมือถือ แขกชำระเงินด้วยบัตรที่บันทึกไว้ผ่าน Google Account — ไม่จำเป็นต้องติดตั้งแอป',
        },
        {
          title: 'การเชื่อมต่อ UPI (อินเดีย)',
          description: 'ในอินเดีย Google Pay คือแอป UPI อันดับ 1 ที่มีผู้ใช้กว่า 200 ล้านคน รับชำระเงิน UPI ได้อย่างราบรื่นผ่านการเชื่อมต่อ Google Pay สำหรับแขกชาวอินเดีย',
        },
        {
          title: 'ลดการละทิ้งการจอง',
          description: 'Google Pay ลดความยุ่งยากในการชำระเงินได้ถึง 40% ข้อมูลการชำระเงินที่กรอกอัตโนมัติและการยืนยันแตะครั้งเดียวช่วยลดอัตราการละทิ้งการจอง',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'เปิดใช้งานผ่านช่องทางชำระเงิน',
          description: 'Google Pay เปิดใช้งานผ่านการเชื่อมต่อ Stripe, PayPal, หรือ PaymentHooks ที่มีอยู่ของคุณ ไม่จำเป็นต้องมีบัญชีพ่อค้า Google แยกต่างหากสำหรับการตั้งค่าส่วนใหญ่',
        },
        {
          step: 2,
          title: 'กำหนดกฎการแสดงผล',
          description: 'เลือกว่า Google Pay จะปรากฏที่ไหน: การชำระเงินที่หน้าจอ checkout ของระบบจอง, การชำระเงินที่แผนกต้อนรับ, ค่าบริการในพอร์ทัลแขก, และการชำระเงินผ่านลิงก์',
        },
        {
          step: 3,
          title: 'ตั้งค่าการตั้งค่าสกุลเงิน',
          description: 'กำหนดค่าสกุลเงินที่รองรับและการตั้งค่าภาษาท้องถิ่น Google Pay ตรวจจับวิธีการชำระเงินที่แขกชื่นชอบตามภูมิภาคโดยอัตโนมัติ',
        },
        {
          step: 4,
          title: 'เริ่มใช้งาน',
          description: 'ปุ่ม Google Pay จะปรากฏสำหรับแขกที่มีคุณสมบัติบนอุปกรณ์ Android, Chrome browser, และระบบจองของคุณ ชำระเงินได้ทันที',
        },
      ],
      features: [
        {
          title: 'ปุ่มในระบบจอง',
          description: 'ปุ่ม Google Pay บนหน้าชำระเงินของ BookMyRoom.io ผู้ใช้ Android และ Chrome จะเห็นตัวเลือก GPay ที่คุ้นเคยสำหรับการชำระเงินทันที',
        },
        {
          title: 'การชำระเงิน UPI (อินเดีย)',
          description: 'รับธุรกรรม Google Pay UPI สำหรับแขกชาวอินเดีย รองรับทั้งการสแกนและชำระเงิน และการชำระเงิน UPI แบบ intent-based ผ่าน PaymentHooks',
        },
        {
          title: 'รองรับเครื่องรูดบัตร NFC',
          description: 'รับการชำระเงินแบบแตะผ่าน Google Pay ที่แผนกต้อนรับด้วยเครื่องรูดบัตรที่รองรับ NFC ประสบการณ์เช็คอินและเช็คเอาท์แบบไม่ต้องสัมผัส',
        },
        {
          title: 'กรอกบัตรที่บันทึกไว้อัตโนมัติ',
          description: 'แขกชำระเงินด้วยบัตรที่บันทึกไว้ใน Google Account ไม่จำเป็นต้องกรอกข้อมูลด้วยตนเอง — เพียงเลือกบัตรและยืนยันตัวตน',
        },
        {
          title: 'รองรับหลายแพลตฟอร์ม',
          description: 'Google Pay ทำงานบนโทรศัพท์ Android, แท็บเล็ต, นาฬิกา Wear OS และ Chrome browser บนระบบปฏิบัติการใดก็ได้ รวมถึง Windows, Mac และ Linux',
        },
        {
          title: 'การวิเคราะห์การชำระเงิน',
          description: 'ติดตามอัตราการใช้งาน Google Pay, ปริมาณธุรกรรม, และมูลค่าการจองเฉลี่ยควบคู่กับวิธีการชำระเงินอื่นๆ ทั้งหมดในแดชบอร์ดการเงิน',
        },
      ],
      faqItems: [
        {
          question: 'ฉันต้องมีบัญชีพ่อค้า Google หรือไม่?',
          answer: 'สำหรับการตั้งค่าส่วนใหญ่ ไม่จำเป็น Google Pay เปิดใช้งานผ่านการเชื่อมต่อ Stripe, PayPal, หรือ PaymentHooks ที่มีอยู่ของคุณ หากคุณต้องการฟีเจอร์ Google Pay ขั้นสูง สามารถสร้างบัญชี Google Pay Business Console ได้โดยไม่มีค่าใช้จ่าย',
        },
        {
          question: 'Google Pay ทำงานร่วมกับ UPI ในอินเดียได้หรือไม่?',
          answer: 'ได้ Google Pay คือแอป UPI อันดับ 1 ในอินเดียที่มีผู้ใช้กว่า 200 ล้านคน เมื่อรวมกับการเชื่อมต่อ PaymentHooks คุณสามารถรับชำระเงิน Google Pay UPI ได้อย่างราบรื่นสำหรับแขกชาวอินเดีย',
        },
        {
          question: 'มีค่าธรรมเนียมเพิ่มเติมสำหรับ Google Pay หรือไม่?',
          answer: 'Google ไม่เรียกเก็บค่าธรรมเนียมเพิ่มเติมจากพ่อค้าสำหรับธุรกรรม Google Pay คุณจ่ายค่าธรรมเนียมมาตรฐานของช่องทางชำระเงินของคุณ (Stripe, PayPal, หรือ PaymentHooks) ไม่มีค่าธรรมเนียมเพิ่มเติมจาก HotelStack.io',
        },
        {
          question: 'แขกกี่เปอร์เซ็นต์ที่ใช้ Google Pay?',
          answer: 'การใช้งานแตกต่างกันตามตลาด ในอินเดีย 40-50% ของการชำระเงินผ่านมือถือใช้ Google Pay (UPI) ทั่วโลก 15-25% ของผู้ใช้ Android มีการตั้งค่า Google Pay โดยมีการใช้งานสูงกว่าในเอเชียตะวันออกเฉียงใต้และยุโรป',
        },
      ],
    },
    es: {
      partnerType: 'Pago Contactless',
      heroTitle: 'Integración de HotelStack.io con Google Pay',
      heroSubtitle: 'Acepta pagos contactless de más de 150 millones de usuarios de Google Pay en 40+ países. Pago fluido en dispositivos Android, navegador Chrome y servicios de Google.',
      benefits: [
        {
          title: 'Más de 150M de usuarios en 40+ países',
          description: 'Google Pay es la solución de pago móvil líder en Android. Llega a la enorme base de usuarios de Android con pago de un toque a través de web y móvil.',
        },
        {
          title: 'Pagos en Chrome Browser',
          description: 'Google Pay funciona de forma nativa en Chrome browser en escritorio y móvil. Los huéspedes pagan con tarjetas guardadas en su Google Account — sin necesidad de instalar ninguna app.',
        },
        {
          title: 'Integración UPI (India)',
          description: 'En India, Google Pay es la app UPI #1 con más de 200M de usuarios. Acepta pagos UPI sin inconvenientes a través de la integración de Google Pay para huéspedes nacionales indios.',
        },
        {
          title: 'Reducción del abandono de reservas',
          description: 'Google Pay reduce la fricción en el proceso de pago en un 40%. Las credenciales de pago completadas automáticamente y la confirmación de un toque minimizan las tasas de abandono.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'Activar vía pasarela de pago',
          description: 'Google Pay se activa a través de tu integración existente con Stripe, PayPal o PaymentHooks. No se requiere cuenta de comerciante Google separada para la mayoría de configuraciones.',
        },
        {
          step: 2,
          title: 'Configurar reglas de visualización',
          description: 'Elige dónde aparece Google Pay: checkout del motor de reservas, pagos en recepción, cargos en portal de huéspedes, y flujos de enlaces de pago.',
        },
        {
          step: 3,
          title: 'Establecer preferencias de moneda',
          description: 'Configura las monedas admitidas y la configuración de localización. Google Pay detecta automáticamente el método de pago preferido del huésped según la región.',
        },
        {
          step: 4,
          title: 'Ir en vivo',
          description: 'El botón de Google Pay aparece para los huéspedes elegibles en dispositivos Android, Chrome browser y tu motor de reservas. Los pagos se procesan al instante.',
        },
      ],
      features: [
        {
          title: 'Botón en el motor de reservas',
          description: 'Botón de Google Pay en tu checkout de BookMyRoom.io. Los usuarios de Android y Chrome ven la familiar opción de GPay para pago instantáneo.',
        },
        {
          title: 'Pagos UPI (India)',
          description: 'Acepta transacciones Google Pay UPI para huéspedes indios. Compatible con flujos UPI de escaneo y pago, así como basados en intent a través de PaymentHooks.',
        },
        {
          title: 'Soporte para terminal NFC',
          description: 'Acepta pagos Google Pay tap-to-pay en tu recepción con terminales de pago habilitados para NFC. Experiencia de check-in y check-out sin contacto.',
        },
        {
          title: 'Autocompletado de tarjetas guardadas',
          description: 'Los huéspedes pagan con tarjetas guardadas en su Google Account. Sin necesidad de ingresar datos manualmente — solo selecciona una tarjeta y autentícate.',
        },
        {
          title: 'Compatibilidad multiplataforma',
          description: 'Google Pay funciona en teléfonos Android, tablets, relojes Wear OS y Chrome browser en cualquier sistema operativo, incluyendo Windows, Mac y Linux.',
        },
        {
          title: 'Analítica de pagos',
          description: 'Rastrea las tasas de adopción de Google Pay, volúmenes de transacciones y valores promedio de reserva junto a todos los demás métodos de pago en tu panel financiero.',
        },
      ],
      faqItems: [
        {
          question: '¿Necesito una cuenta de comerciante de Google?',
          answer: 'Para la mayoría de configuraciones, no. Google Pay se activa a través de tu integración existente con Stripe, PayPal o PaymentHooks. Si necesitas funciones avanzadas de Google Pay, puedes configurar una cuenta de Google Pay Business Console sin costo.',
        },
        {
          question: '¿Google Pay funciona con UPI en India?',
          answer: 'Sí. Google Pay es la app UPI #1 en India con más de 200M de usuarios. Combinado con la integración de PaymentHooks, puedes aceptar pagos Google Pay UPI sin problemas para huéspedes nacionales indios.',
        },
        {
          question: '¿Hay cargos adicionales por Google Pay?',
          answer: 'Google no cobra a los comerciantes cargos adicionales por transacciones con Google Pay. Pagas las tarifas estándar de tu pasarela de pago (Stripe, PayPal o PaymentHooks). No se aplica ningún recargo de HotelStack.io.',
        },
        {
          question: '¿Qué porcentaje de huéspedes usa Google Pay?',
          answer: 'La adopción varía según el mercado. En India, el 40-50% de los pagos móviles usa Google Pay (UPI). A nivel global, el 15-25% de los usuarios de Android tienen Google Pay configurado, con mayor adopción en el Sudeste Asiático y Europa.',
        },
      ],
    },
    fr: {
      partnerType: 'Paiement sans contact',
      heroTitle: 'Intégration HotelStack.io + Google Pay',
      heroSubtitle: 'Acceptez des paiements sans contact de plus de 150 millions d\'utilisateurs Google Pay dans 40+ pays. Paiement fluide sur les appareils Android, le navigateur Chrome et les services Google.',
      benefits: [
        {
          title: 'Plus de 150M d\'utilisateurs dans 40+ pays',
          description: 'Google Pay est la principale solution de paiement mobile sur Android. Atteignez la vaste base d\'utilisateurs Android avec un paiement en un geste sur le web et le mobile.',
        },
        {
          title: 'Paiements via Chrome Browser',
          description: 'Google Pay fonctionne nativement dans Chrome browser sur ordinateur et mobile. Les clients paient avec leurs cartes enregistrées via leur Google Account — aucune installation d\'application requise.',
        },
        {
          title: 'Intégration UPI (Inde)',
          description: 'En Inde, Google Pay est l\'application UPI n°1 avec plus de 200M d\'utilisateurs. Acceptez les paiements UPI sans friction grâce à l\'intégration Google Pay pour les clients locaux indiens.',
        },
        {
          title: 'Réduction de l\'abandon de réservation',
          description: 'Google Pay réduit les frictions de paiement de 40%. Les identifiants de paiement préremplis et la confirmation en un geste minimisent les taux d\'abandon de réservation.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'Activation via la passerelle de paiement',
          description: 'Google Pay est activé via votre intégration Stripe, PayPal ou PaymentHooks existante. Aucun compte marchand Google séparé n\'est requis pour la plupart des configurations.',
        },
        {
          step: 2,
          title: 'Configuration des règles d\'affichage',
          description: 'Choisissez où Google Pay apparaît : checkout du moteur de réservation, paiements à la réception, frais sur le portail client, et flux de liens de paiement.',
        },
        {
          step: 3,
          title: 'Définir les préférences de devise',
          description: 'Configurez les devises supportées et les paramètres de localisation. Google Pay détecte automatiquement le mode de paiement préféré du client selon la région.',
        },
        {
          step: 4,
          title: 'Mise en ligne',
          description: 'Le bouton Google Pay apparaît pour les clients éligibles sur les appareils Android, Chrome browser et votre moteur de réservation. Les paiements sont traités instantanément.',
        },
      ],
      features: [
        {
          title: 'Bouton dans le moteur de réservation',
          description: 'Bouton Google Pay sur votre checkout BookMyRoom.io. Les utilisateurs Android et Chrome voient l\'option GPay familière pour un paiement instantané.',
        },
        {
          title: 'Paiements UPI (Inde)',
          description: 'Acceptez les transactions Google Pay UPI pour les clients indiens. Supporte les flux UPI par scan et par intent à travers PaymentHooks.',
        },
        {
          title: 'Support terminal NFC',
          description: 'Acceptez les paiements Google Pay tap-to-pay à votre réception avec des terminaux de paiement NFC. Expérience de check-in et check-out sans contact.',
        },
        {
          title: 'Remplissage automatique des cartes enregistrées',
          description: 'Les clients paient avec les cartes enregistrées dans leur Google Account. Aucune saisie manuelle requise — sélectionnez simplement une carte et authentifiez-vous.',
        },
        {
          title: 'Compatibilité multiplateforme',
          description: 'Google Pay fonctionne sur les téléphones Android, tablettes, montres Wear OS et Chrome browser sur tous les systèmes d\'exploitation, y compris Windows, Mac et Linux.',
        },
        {
          title: 'Analytique des paiements',
          description: 'Suivez les taux d\'adoption de Google Pay, les volumes de transactions et les valeurs moyennes des réservations en parallèle de tous les autres moyens de paiement dans votre tableau de bord financier.',
        },
      ],
      faqItems: [
        {
          question: 'Ai-je besoin d\'un compte marchand Google ?',
          answer: 'Pour la plupart des configurations, non. Google Pay est activé via votre intégration Stripe, PayPal ou PaymentHooks existante. Si vous avez besoin de fonctionnalités avancées, un compte Google Pay Business Console peut être créé gratuitement.',
        },
        {
          question: 'Google Pay fonctionne-t-il avec UPI en Inde ?',
          answer: 'Oui. Google Pay est l\'application UPI n°1 en Inde avec plus de 200M d\'utilisateurs. Combiné à l\'intégration PaymentHooks, vous pouvez accepter les paiements Google Pay UPI sans friction pour les clients locaux indiens.',
        },
        {
          question: 'Y a-t-il des frais supplémentaires pour Google Pay ?',
          answer: 'Google ne facture pas de frais supplémentaires aux marchands pour les transactions Google Pay. Vous payez les frais standards de votre passerelle de paiement (Stripe, PayPal ou PaymentHooks). Aucun surcoût HotelStack.io ne s\'applique.',
        },
        {
          question: 'Quel pourcentage de clients utilise Google Pay ?',
          answer: 'L\'adoption varie selon les marchés. En Inde, 40 à 50% des paiements mobiles utilisent Google Pay (UPI). À l\'échelle mondiale, 15 à 25% des utilisateurs Android ont Google Pay configuré, avec une adoption plus élevée en Asie du Sud-Est et en Europe.',
        },
      ],
    },
    de: {
      partnerType: 'Kontaktloses Bezahlen',
      heroTitle: 'HotelStack.io + Google Pay Integration',
      heroSubtitle: 'Akzeptieren Sie kontaktlose Zahlungen von über 150 Millionen Google Pay-Nutzern in 40+ Ländern. Nahtloser Checkout auf Android-Geräten, Chrome-Browser und Google-Diensten.',
      benefits: [
        {
          title: 'Über 150M Nutzer in 40+ Ländern',
          description: 'Google Pay ist die führende mobile Zahlungslösung auf Android. Erreichen Sie die riesige Android-Nutzerbasis mit Einzel-Tap-Zahlung über Web und Mobil.',
        },
        {
          title: 'Chrome-Browser-Zahlungen',
          description: 'Google Pay funktioniert nativ im Chrome-Browser auf Desktop und Mobilgeräten. Gäste zahlen mit gespeicherten Karten über ihr Google Account — keine App-Installation erforderlich.',
        },
        {
          title: 'UPI-Integration (Indien)',
          description: 'In Indien ist Google Pay die #1 UPI-App mit über 200M Nutzern. Akzeptieren Sie UPI-Zahlungen nahtlos über die Google Pay-Integration für indische Inlandsgäste.',
        },
        {
          title: 'Reduzierte Warenkorbabbrüche',
          description: 'Google Pay reduziert die Zahlungsreibung um 40%. Automatisch ausgefüllte Zahlungsdaten und Einzel-Tap-Bestätigung minimieren die Abbruchraten bei Buchungen.',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'Aktivierung über Zahlungsgateway',
          description: 'Google Pay wird über Ihre bestehende Stripe-, PayPal- oder PaymentHooks-Integration aktiviert. Für die meisten Konfigurationen ist kein separates Google-Händlerkonto erforderlich.',
        },
        {
          step: 2,
          title: 'Anzeigeregeln konfigurieren',
          description: 'Wählen Sie, wo Google Pay erscheint: Buchungsmodul-Checkout, Rezeptionszahlungen, Gastportal-Gebühren und Zahlungslink-Flows.',
        },
        {
          step: 3,
          title: 'Währungspräferenzen festlegen',
          description: 'Konfigurieren Sie unterstützte Währungen und Lokalisierungseinstellungen. Google Pay erkennt automatisch die bevorzugte Zahlungsmethode des Gastes nach Region.',
        },
        {
          step: 4,
          title: 'Live gehen',
          description: 'Die Google Pay-Schaltfläche erscheint für berechtigte Gäste auf Android-Geräten, Chrome-Browser und Ihrem Buchungsmodul. Zahlungen werden sofort verarbeitet.',
        },
      ],
      features: [
        {
          title: 'Schaltfläche im Buchungsmodul',
          description: 'Google Pay-Schaltfläche auf Ihrem BookMyRoom.io-Checkout. Android- und Chrome-Nutzer sehen die vertraute GPay-Option für sofortige Zahlung.',
        },
        {
          title: 'UPI-Zahlungen (Indien)',
          description: 'Akzeptieren Sie Google Pay UPI-Transaktionen für indische Gäste. Unterstützt sowohl Scan-and-Pay- als auch Intent-basierte UPI-Flows über PaymentHooks.',
        },
        {
          title: 'NFC-Terminal-Unterstützung',
          description: 'Akzeptieren Sie Google Pay Tap-to-Pay an Ihrer Rezeption mit NFC-fähigen Zahlungsterminals. Kontaktloses Check-in und Check-out-Erlebnis.',
        },
        {
          title: 'Gespeicherte Karten automatisch ausfüllen',
          description: 'Gäste zahlen mit Karten, die in ihrem Google Account gespeichert sind. Keine manuelle Eingabe nötig — einfach Karte auswählen und authentifizieren.',
        },
        {
          title: 'Plattformübergreifende Unterstützung',
          description: 'Google Pay funktioniert auf Android-Smartphones, Tablets, Wear OS-Uhren und Chrome-Browser auf jedem Betriebssystem, einschließlich Windows, Mac und Linux.',
        },
        {
          title: 'Zahlungsanalysen',
          description: 'Verfolgen Sie Google Pay-Adoptionsraten, Transaktionsvolumina und durchschnittliche Buchungswerte neben allen anderen Zahlungsmethoden in Ihrem Finanz-Dashboard.',
        },
      ],
      faqItems: [
        {
          question: 'Benötige ich ein Google-Händlerkonto?',
          answer: 'Für die meisten Konfigurationen nein. Google Pay wird über Ihre bestehende Stripe-, PayPal- oder PaymentHooks-Integration aktiviert. Wenn Sie erweiterte Google Pay-Funktionen benötigen, kann ein Google Pay Business Console-Konto kostenlos eingerichtet werden.',
        },
        {
          question: 'Funktioniert Google Pay mit UPI in Indien?',
          answer: 'Ja. Google Pay ist die #1 UPI-App in Indien mit über 200M Nutzern. In Kombination mit der PaymentHooks-Integration können Sie Google Pay UPI-Zahlungen nahtlos für indische Inlandsgäste akzeptieren.',
        },
        {
          question: 'Gibt es zusätzliche Gebühren für Google Pay?',
          answer: 'Google erhebt keine zusätzlichen Gebühren von Händlern für Google Pay-Transaktionen. Sie zahlen die Standardgebühren Ihres Zahlungsgateways (Stripe, PayPal oder PaymentHooks). Es fällt kein HotelStack.io-Aufschlag an.',
        },
        {
          question: 'Wie viel Prozent der Gäste nutzen Google Pay?',
          answer: 'Die Nutzung variiert je nach Markt. In Indien nutzen 40-50% der mobilen Zahlungen Google Pay (UPI). Weltweit haben 15-25% der Android-Nutzer Google Pay konfiguriert, mit höherer Nutzung in Südostasien und Europa.',
        },
      ],
    },
    ja: {
      partnerType: 'タッチレス決済',
      heroTitle: 'HotelStack.io + Google Pay 連携',
      heroSubtitle: '40カ国以上の1億5000万人以上のGoogle Payユーザーからのタッチレス決済を受け付けます。Androidデバイス、Chromeブラウザ、Googleサービスでシームレスなチェックアウトを実現します。',
      benefits: [
        {
          title: '40カ国以上で1億5000万人以上のユーザー',
          description: 'Google PayはAndroidにおける主要なモバイル決済ソリューションです。ウェブとモバイル全体でワンタップ決済により、巨大なAndroidユーザーベースにリーチできます。',
        },
        {
          title: 'Chromeブラウザ決済',
          description: 'Google PayはデスクトップとモバイルのChromeブラウザでネイティブに動作します。ゲストはGoogle Accountに保存されたカードで支払い可能 — アプリのインストールは不要です。',
        },
        {
          title: 'UPI連携（インド）',
          description: 'インドでは、Google PayはUPIアプリ第1位であり、2億人以上のユーザーを抱えています。Google Pay連携を通じてインドの国内ゲスト向けにUPI決済をシームレスに受け付けられます。',
        },
        {
          title: 'カート離脱率の低減',
          description: 'Google Payは決済の摩擦を40%削減します。自動入力された決済情報とワンタップ確認により、予約の離脱率を最小限に抑えます。',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: '決済ゲートウェイ経由で有効化',
          description: 'Google Payは既存のStripe、PayPal、またはPaymentHooks連携を通じて有効化されます。ほとんどの設定ではGoogleの独立した加盟店アカウントは不要です。',
        },
        {
          step: 2,
          title: '表示ルールを設定',
          description: 'Google Payが表示される場所を選択します：予約エンジンのチェックアウト、フロントデスクの支払い、ゲストポータルの料金、支払いリンクフロー。',
        },
        {
          step: 3,
          title: '通貨設定を構成',
          description: '対応通貨とローカライズ設定を構成します。Google Payはゲストの地域に応じて優先決済方法を自動検出します。',
        },
        {
          step: 4,
          title: '本番稼働',
          description: 'Google Payボタンが対象ゲストのAndroidデバイス、Chromeブラウザ、予約エンジンに表示されます。支払いは即座に処理されます。',
        },
      ],
      features: [
        {
          title: '予約エンジンボタン',
          description: 'BookMyRoom.ioのチェックアウトにGoogle Payボタンを配置。AndroidとChromeのユーザーには即時決済のためのおなじみのGPayオプションが表示されます。',
        },
        {
          title: 'UPI決済（インド）',
          description: 'インドのゲスト向けにGoogle Pay UPIトランザクションを受け付けます。PaymentHooksを通じたスキャン支払いとインテントベースのUPIフローの両方をサポートします。',
        },
        {
          title: 'NFCターミナル対応',
          description: 'NFC対応の決済端末でフロントデスクでのGoogle Payタップ決済を受け付けます。タッチレスなチェックイン・チェックアウト体験を提供します。',
        },
        {
          title: '保存カードの自動入力',
          description: 'ゲストはGoogle Accountに保存されたカードで支払いが可能です。手動入力不要 — カードを選択して認証するだけです。',
        },
        {
          title: 'クロスプラットフォーム対応',
          description: 'Google PayはAndroidスマートフォン、タブレット、Wear OSウォッチ、そしてWindows、Mac、Linuxを含むあらゆるOSのChromeブラウザで動作します。',
        },
        {
          title: '決済分析',
          description: 'Google Payの採用率、取引量、予約の平均額を、財務ダッシュボードの他のすべての決済方法と並べて追跡できます。',
        },
      ],
      faqItems: [
        {
          question: 'Googleの加盟店アカウントは必要ですか？',
          answer: 'ほとんどの設定では不要です。Google Payは既存のStripe、PayPal、またはPaymentHooks連携を通じて有効化されます。高度なGoogle Pay機能が必要な場合は、Google Pay Business Consoleアカウントを無料で設定できます。',
        },
        {
          question: 'Google PayはインドのUPIと連携しますか？',
          answer: 'はい。Google PayはインドでUPIアプリ第1位であり、2億人以上のユーザーを抱えています。PaymentHooks連携と組み合わせることで、インドの国内ゲスト向けにGoogle Pay UPI決済をシームレスに受け付けられます。',
        },
        {
          question: 'Google Payに追加料金はかかりますか？',
          answer: 'GoogleはGoogle Pay取引に対して加盟店に追加料金を請求しません。決済ゲートウェイ（Stripe、PayPal、またはPaymentHooks）の標準手数料のみをお支払いください。HotelStack.ioの追加料金はありません。',
        },
        {
          question: 'ゲストの何パーセントがGoogle Payを使用していますか？',
          answer: '採用率は市場によって異なります。インドでは、モバイル決済の40〜50%がGoogle Pay（UPI）を使用しています。世界全体では、Androidユーザーの15〜25%がGoogle Payを設定しており、東南アジアやヨーロッパでの採用率が高い傾向にあります。',
        },
      ],
    },
    zh: {
      partnerType: '非接触式支付',
      heroTitle: 'HotelStack.io + Google Pay 集成',
      heroSubtitle: '接受来自40多个国家超过1.5亿Google Pay用户的非接触式付款。在Android设备、Chrome浏览器和Google服务上实现无缝结账。',
      benefits: [
        {
          title: '40多个国家超过1.5亿用户',
          description: 'Google Pay是Android上领先的移动支付解决方案。通过网页和移动端的一键支付，覆盖庞大的Android用户群体。',
        },
        {
          title: 'Chrome浏览器支付',
          description: 'Google Pay在桌面和移动端的Chrome浏览器中原生运行。客人通过Google Account中保存的卡片付款——无需安装任何应用。',
        },
        {
          title: 'UPI集成（印度）',
          description: '在印度，Google Pay是排名第一的UPI应用，拥有超过2亿用户。通过Google Pay集成为印度国内客人无缝接受UPI支付。',
        },
        {
          title: '降低预订放弃率',
          description: 'Google Pay将支付摩擦降低40%。自动填写的支付信息和一键确认最大程度减少预订流失率。',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: '通过支付网关启用',
          description: 'Google Pay通过您现有的Stripe、PayPal或PaymentHooks集成启用。大多数配置无需单独的Google商户账户。',
        },
        {
          step: 2,
          title: '配置显示规则',
          description: '选择Google Pay出现的位置：预订引擎结账、前台收款、客户门户费用，以及支付链接流程。',
        },
        {
          step: 3,
          title: '设置货币偏好',
          description: '配置支持的货币和本地化设置。Google Pay会根据地区自动检测客人的首选支付方式。',
        },
        {
          step: 4,
          title: '正式上线',
          description: 'Google Pay按钮将在符合条件的客人的Android设备、Chrome浏览器和您的预订引擎上显示。支付即时处理完成。',
        },
      ],
      features: [
        {
          title: '预订引擎按钮',
          description: '在BookMyRoom.io结账页面上设置Google Pay按钮。Android和Chrome用户可看到熟悉的GPay选项，实现即时支付。',
        },
        {
          title: 'UPI支付（印度）',
          description: '为印度客人接受Google Pay UPI交易。通过PaymentHooks支持扫码支付和基于意图的UPI流程。',
        },
        {
          title: 'NFC终端支持',
          description: '在前台通过支持NFC的支付终端接受Google Pay的触碰支付。提供非接触式入住和退房体验。',
        },
        {
          title: '保存卡片自动填充',
          description: '客人使用存储在Google Account中的卡片付款。无需手动输入——只需选择卡片并完成验证。',
        },
        {
          title: '跨平台支持',
          description: 'Google Pay适用于Android手机、平板电脑、Wear OS手表以及包括Windows、Mac和Linux在内的任何操作系统上的Chrome浏览器。',
        },
        {
          title: '支付分析',
          description: '在财务仪表板中与所有其他支付方式一起追踪Google Pay的采用率、交易量和平均预订价值。',
        },
      ],
      faqItems: [
        {
          question: '我需要Google商户账户吗？',
          answer: '对于大多数配置而言，不需要。Google Pay通过您现有的Stripe、PayPal或PaymentHooks集成启用。如果您需要高级Google Pay功能，可以免费创建Google Pay Business Console账户。',
        },
        {
          question: 'Google Pay在印度能与UPI配合使用吗？',
          answer: '是的。Google Pay是印度排名第一的UPI应用，拥有超过2亿用户。结合PaymentHooks集成，您可以为印度国内客人无缝接受Google Pay UPI支付。',
        },
        {
          question: 'Google Pay是否有额外费用？',
          answer: 'Google不向商户收取Google Pay交易的额外费用。您只需支付支付网关（Stripe、PayPal或PaymentHooks）的标准费用。不收取任何HotelStack.io附加费。',
        },
        {
          question: '有多少比例的客人使用Google Pay？',
          answer: '采用率因市场而异。在印度，40-50%的移动支付使用Google Pay（UPI）。在全球范围内，15-25%的Android用户已配置Google Pay，在东南亚和欧洲的采用率更高。',
        },
      ],
    },
    hi: {
      partnerType: 'संपर्क रहित भुगतान',
      heroTitle: 'HotelStack.io + Google Pay एकीकरण',
      heroSubtitle: '40+ देशों में 150M+ Google Pay उपयोगकर्ताओं से संपर्क रहित भुगतान स्वीकार करें। Android उपकरणों, Chrome ब्राउज़र और Google सेवाओं पर निर्बाध चेकआउट।',
      benefits: [
        {
          title: '40+ देशों में 150M+ उपयोगकर्ता',
          description: 'Google Pay, Android पर अग्रणी मोबाइल भुगतान समाधान है। वेब और मोबाइल पर वन-टैप भुगतान से विशाल Android उपयोगकर्ता आधार तक पहुंचें।',
        },
        {
          title: 'Chrome ब्राउज़र भुगतान',
          description: 'Google Pay, डेस्कटॉप और मोबाइल पर Chrome ब्राउज़र में स्वदेशी रूप से काम करता है। अतिथि Google Account के माध्यम से सहेजे गए कार्डों से भुगतान करते हैं — कोई ऐप इंस्टॉलेशन आवश्यक नहीं।',
        },
        {
          title: 'UPI एकीकरण (भारत)',
          description: 'भारत में, Google Pay 200M+ उपयोगकर्ताओं के साथ #1 UPI ऐप है। भारतीय घरेलू अतिथियों के लिए Google Pay एकीकरण के माध्यम से निर्बाध रूप से UPI भुगतान स्वीकार करें।',
        },
        {
          title: 'बुकिंग परित्याग में कमी',
          description: 'Google Pay, चेकआउट की बाधाओं को 40% कम करता है। स्वतः भरे गए भुगतान क्रेडेंशियल और वन-टैप पुष्टिकरण बुकिंग छोड़ने की दरों को न्यूनतम करते हैं।',
        },
      ],
      howItWorks: [
        {
          step: 1,
          title: 'भुगतान गेटवे के माध्यम से सक्षम करें',
          description: 'Google Pay आपके मौजूदा Stripe, PayPal, या PaymentHooks एकीकरण के माध्यम से सक्षम किया जाता है। अधिकांश सेटअप के लिए अलग Google merchant खाते की आवश्यकता नहीं है।',
        },
        {
          step: 2,
          title: 'प्रदर्शन नियम कॉन्फ़िगर करें',
          description: 'चुनें कि Google Pay कहाँ दिखाई दे: बुकिंग इंजन चेकआउट, फ्रंट डेस्क भुगतान, अतिथि पोर्टल शुल्क, और भुगतान लिंक प्रवाह।',
        },
        {
          step: 3,
          title: 'मुद्रा प्राथमिकताएं सेट करें',
          description: 'समर्थित मुद्राओं और स्थानीयकरण सेटिंग्स को कॉन्फ़िगर करें। Google Pay क्षेत्र के अनुसार अतिथि की पसंदीदा भुगतान विधि को स्वतः पहचान लेता है।',
        },
        {
          step: 4,
          title: 'लाइव जाएं',
          description: 'Google Pay बटन Android उपकरणों, Chrome ब्राउज़र और आपके बुकिंग इंजन पर पात्र अतिथियों के लिए दिखाई देता है। भुगतान तुरंत संसाधित होते हैं।',
        },
      ],
      features: [
        {
          title: 'बुकिंग इंजन बटन',
          description: 'आपके BookMyRoom.io चेकआउट पर Google Pay बटन। Android और Chrome उपयोगकर्ता तत्काल भुगतान के लिए परिचित GPay विकल्प देखते हैं।',
        },
        {
          title: 'UPI भुगतान (भारत)',
          description: 'भारतीय अतिथियों के लिए Google Pay UPI लेनदेन स्वीकार करें। PaymentHooks के माध्यम से स्कैन-एंड-पे और इंटेंट-आधारित UPI प्रवाह दोनों का समर्थन करता है।',
        },
        {
          title: 'NFC टर्मिनल समर्थन',
          description: 'NFC-सक्षम भुगतान टर्मिनलों के साथ अपने फ्रंट डेस्क पर Google Pay टैप-टू-पे स्वीकार करें। संपर्क रहित चेक-इन और चेक-आउट अनुभव।',
        },
        {
          title: 'सहेजे गए कार्डों का स्वत: भरण',
          description: 'अतिथि अपने Google Account में सहेजे गए कार्डों से भुगतान करते हैं। मैन्युअल प्रविष्टि की आवश्यकता नहीं — बस एक कार्ड चुनें और प्रमाणित करें।',
        },
        {
          title: 'क्रॉस-प्लेटफ़ॉर्म समर्थन',
          description: 'Google Pay, Android फोन, टैबलेट, Wear OS घड़ियों और Windows, Mac और Linux सहित किसी भी ऑपरेटिंग सिस्टम पर Chrome ब्राउज़र पर काम करता है।',
        },
        {
          title: 'भुगतान एनालिटिक्स',
          description: 'अपने वित्त डैशबोर्ड में अन्य सभी भुगतान विधियों के साथ Google Pay अपनाने की दर, लेनदेन वॉल्यूम और औसत बुकिंग मूल्यों को ट्रैक करें।',
        },
      ],
      faqItems: [
        {
          question: 'क्या मुझे Google merchant खाते की आवश्यकता है?',
          answer: 'अधिकांश सेटअप के लिए, नहीं। Google Pay आपके मौजूदा Stripe, PayPal, या PaymentHooks एकीकरण के माध्यम से सक्षम किया जाता है। यदि आपको उन्नत Google Pay सुविधाओं की आवश्यकता है, तो Google Pay Business Console खाता बिना किसी लागत के स्थापित किया जा सकता है।',
        },
        {
          question: 'क्या Google Pay भारत में UPI के साथ काम करता है?',
          answer: 'हाँ। Google Pay, 200M+ उपयोगकर्ताओं के साथ भारत में #1 UPI ऐप है। PaymentHooks एकीकरण के साथ संयुक्त होने पर, आप भारतीय घरेलू अतिथियों के लिए Google Pay UPI भुगतान निर्बाध रूप से स्वीकार कर सकते हैं।',
        },
        {
          question: 'क्या Google Pay के लिए अतिरिक्त शुल्क हैं?',
          answer: 'Google, Google Pay लेनदेन के लिए व्यापारियों से अतिरिक्त शुल्क नहीं लेता है। आप अपने भुगतान गेटवे (Stripe, PayPal, या PaymentHooks) के मानक शुल्क का भुगतान करते हैं। कोई HotelStack.io अधिभार लागू नहीं होता है।',
        },
        {
          question: 'कितने प्रतिशत अतिथि Google Pay का उपयोग करते हैं?',
          answer: 'अपनाने की दर बाजार के अनुसार भिन्न होती है। भारत में, 40-50% मोबाइल भुगतान Google Pay (UPI) का उपयोग करते हैं। वैश्विक स्तर पर, 15-25% Android उपयोगकर्ताओं के पास Google Pay कॉन्फ़िगर है, दक्षिण पूर्व एशिया और यूरोप में उच्च अपनाने के साथ।',
        },
      ],
    },
  },
};
